/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import * as d3 from 'd3';
import $ from 'jquery';

let svg = null;
let ranges = null;
let x = null;
let y = null;
let height = null;

export const createArea = function(settings) {
  const deferred = new $.Deferred();

  const divId = settings.el;
  const margin = settings.margin;
  ranges = settings.ranges;
  const parent = d3.select('#' + divId);

  // Add class to parent div so that our css selectors can be scoped
  parent.classed('areas-chart', true);

  //   var bbox = parent.node().getBoundingClientRect();
  const width = 270;
  height = 270;

  const xMin = 10;
  const xMax = 130;
  x = d3.scale.linear().range([0, width]);
  x.domain([xMin, xMax]);

  const yMin = 80;
  const yMax = 200;
  y = d3.scale.linear().range([height, 0]);
  y.domain([yMin, yMax]);

  const xAxis = d3.svg
    .axis()
    .scale(x)
    .orient('bottom')
    .tickSize(0)
    .tickPadding(16);

  const yAxis = d3.svg
    .axis()
    .scale(y)
    .orient('left')
    .tickSize(0)
    .tickPadding(16);

  svg = parent
    .append('svg')
    .attr('overflow', 'visible')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

  // Axes labels
  // TODO: read axes labels from data
  svg
    .append('text')
    .attr('x', 250)
    .attr('y', height + 65)
    .attr('fill', '#8D919F')
    .attr('font-family', 'GTAR')
    .attr('font-size', 16)
    .attr('text-anchor', 'end')
    .text('Diastolic Blood Pressure (DBP)');

  svg
    .append('text')
    .attr('transform', 'rotate(270)')
    .attr('x', -40)
    .attr('y', -65)
    .attr('fill', '#8D919F')
    .attr('font-family', 'GTAR')
    .attr('font-size', 16)
    .attr('text-anchor', 'end')
    .text('Systolic Blood Pressure (SBP)');

  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i];

    svg
      .append('rect')
      .attr('id', 'guide_' + i)
      .attr('y', y(range.y.max))
      .attr('width', x(range.x.max))
      .attr('height', height - y(range.y.max))
      .attr('stroke-width', 2)
      .attr('stroke', '#4D4D4F')
      .attr('fill', 'none');

    svg
      .append('text')
      .attr('id', 'label_' + i)
      .text(range.interpretation)
      .attr('y', y(range.y.max) + 30)
      .attr('x', 10)
      .attr('fill', '#4D4D4F')
      .attr('font-family', 'GTAR')
      .attr('font-size', '16px');
  }

  svg
    .append('rect')
    .attr('id', 'indicator-rect')
    .attr('width', 0)
    .attr('height', 0)
    .attr('stroke-width', 3)
    .attr('fill', 'none');

  svg
    .append('circle')
    .attr('id', 'datum')
    .attr('cx', 0)
    .attr('cy', height)
    .attr('fill', 'black');

  svg
    .append('g')
    .attr('class', 'x axis')
    .attr('font-family', 'GTAR')
    .attr('font-size', '12px')
    .attr('transform', 'translate(0,' + (height - 1) + ')')
    .call(xAxis);

  svg
    .append('g')
    .attr('class', 'y axis')
    .attr('stroke-width', 3)
    .attr('font-family', 'GTAR')
    .attr('font-size', '12px')
    .call(yAxis);

  deferred.resolve('resolved');
  return deferred;
};

export const updateArea = function(data) {
  let currentRange = null;
  let currentPos = null;

  for (let i = ranges.length - 1; i >= 0; i--) {
    const range = ranges[i];

    // Start from highest range, work down
    if (data.x <= range.x.max && data.y <= range.y.max) {
      currentRange = range;
      currentPos = i;
    }

    // Reset colors to gray
    svg.select('#label_' + i).attr('fill', '#4D4D4F');
  }

  // Update value position
  svg
    .select('#datum')
    .transition(1000)
    .attr('cx', x(data.x))
    .attr('cy', y(data.y))
    .attr('r', 5)
    .attr('fill', '#000000')
    .attr('stroke-width', 0);

  // If value is within expected ranges, highlight current range
  if (currentRange) {
    svg
      .select('#indicator-rect')
      .transition(1000)
      .attr('height', height - y(currentRange.y.max))
      .attr('y', y(currentRange.y.max))
      .attr('width', x(currentRange.x.max))
      .attr('stroke', data.color);

    svg.select('#label_' + currentPos).attr('fill', data.color);
  } else {
    // Data outside of expected ranges, don't highlight any range
    svg.select('#indicator-rect').attr('stroke', 'none');

    // If data is outside of chart area, hide dot
    const firstRange = ranges[0];
    const lastRange = ranges[ranges.length - 1];

    if (
      data.x < 40 ||
      data.x > lastRange.x.max ||
      data.y < 80 ||
      data.y > lastRange.y.max ||
      data.y > firstRange.y.min ||
      data.x > firstRange.x.min
    ) {
      svg.select('#datum').attr('fill', 'none');
    }
  }
};
